import React, { createContext, PropsWithChildren, useContext } from 'react';
import { useSearchParams } from 'next/navigation';

import { GetResultsProcessResponse } from '../api';
import { usePrint } from '../hooks/usePrint';

type ResultsContextProps = {
  isCurrentProccess?: boolean;
  onLoadProcess: (process: GetResultsProcessResponse) => void;
} & ReturnType<typeof usePrint>;
const ResultsContext = createContext<ResultsContextProps | undefined>(
  undefined
);

export const ResultsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [process, setProcess] =
    React.useState<GetResultsProcessResponse | null>(null);

  const query = useSearchParams();
  const queryProcessId = query?.get('processId');

  const isCurrentProccess = React.useMemo(() => {
    if (!queryProcessId) {
      return false;
    }
    const parsedProcessId = Number(queryProcessId);

    if (Number.isNaN(parsedProcessId)) {
      return false;
    }
    return parsedProcessId === process?.pId && process?.isCurrentProcess;
  }, [process, queryProcessId]);

  const onLoadProcess = React.useCallback((p: GetResultsProcessResponse) => {
    setProcess(p);
  }, []);

  const value = usePrint();

  const memoizedValue = React.useMemo(
    () => ({
      ...value,
      isCurrentProccess,
      onLoadProcess,
    }),
    [value, isCurrentProccess, onLoadProcess]
  );

  return (
    <ResultsContext.Provider value={memoizedValue}>
      {children}
    </ResultsContext.Provider>
  );
};

export function useResults() {
  const context = useContext(ResultsContext);

  if (!context) {
    throw new Error('useResults() must be used within an ResultsProvider');
  }

  return context;
}
